import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Carousel } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShippingFast, faLock, faHeadset, faArrowRight, faHeart as fasHeart, faTag, faPrint, faShieldHeart, faPalette, faStar, faSpinner, faShoppingBag } from '@fortawesome/free-solid-svg-icons';
import { faHeart as farHeart } from '@fortawesome/free-regular-svg-icons';
import { motion, useAnimation } from 'framer-motion';
import { useFavorites } from '../context/FavoritesContext';
import './Home.css';
import api from '../config/api';
import { toast } from 'react-toastify';
import CampaignPopup from '../components/CampaignPopup';
import { useCollectionTracking } from '../hooks/useCollectionTracking';
import { useProductTracking } from '../hooks/useProductTracking';

const Home = () => {
  const [latestProducts, setLatestProducts] = useState([]);
  const [customDesigns, setCustomDesigns] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  
  // Analytics tracking
  const { trackCollectionClick } = useCollectionTracking();
  
  // Loading state için değişkenler
  const [isLatestProductsLoading, setIsLatestProductsLoading] = useState(true);
  const [isCustomDesignsLoading, setIsCustomDesignsLoading] = useState(true);
  const [isFeaturedProductsLoading, setIsFeaturedProductsLoading] = useState(true);
  const [isCollectionsLoading, setIsCollectionsLoading] = useState(true);
  
  const isFirstRender = useRef(true);
  const { addToFavorites, removeFromFavorites, favorites } = useFavorites();
  const { trackProductAddToFavorites, trackProductRemoveFromFavorites, trackProductClick } = useProductTracking();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  
  // Animasyon kontrolcüleri
  const controlsHero = useAnimation();
  const heroRef = useRef(null);
  const [heroVisible, setHeroVisible] = useState(false);

  // Yardımcı fonksiyonlar
  const chunkArray = (arr, size) => {
    return Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
      arr.slice(i * size, i * size + size)
    );
  };

  // Animasyon varyantları
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  
  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  // Scroll olayını dinle ve elementlerin görünürlüğünü kontrol et
  useEffect(() => {
    const checkVisibility = () => {
      const isElementInView = (el, offset = 100) => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return (
          rect.top <= window.innerHeight - offset &&
          rect.bottom >= offset
        );
      };
      
      if (!heroVisible && isElementInView(heroRef.current)) {
        setHeroVisible(true);
        controlsHero.start('visible');
      }
    };
    
    // İlk yükleme kontrolü
    checkVisibility();
    
    // Scroll olayını dinle
    window.addEventListener('scroll', checkVisibility);
    
    // Temizleme fonksiyonu
    return () => {
      window.removeEventListener('scroll', checkVisibility);
    };
  }, [heroVisible, controlsHero]);

  // Video yükleme durumunu kontrol et
  useEffect(() => {
    const video = videoRef.current;
    
    if (video) {
      // Video zaten yüklenmişse
      if (video.readyState >= 3) {
        handleVideoLoaded();
      }
      
      // Video yüklendiğinde
      const loadHandler = () => handleVideoLoaded();
      // Video yüklenemediğinde
      const errorHandler = () => handleVideoError();
      
      video.addEventListener('loadeddata', loadHandler);
      video.addEventListener('error', errorHandler);
      
      // 5 saniye içinde video yüklenmezse fallback'e geç
      const timeoutId = setTimeout(() => {
        if (!isVideoLoaded) {
          console.warn("Video 5 saniye içinde yüklenemedi, fallback'e geçiliyor");
          handleVideoError();
        }
      }, 5000);
      
      // iOS için özel düzenleme - sayfa ziyaret edildiğinde ve görünür olduğunda
      // videoyu manuel olarak oynatmak için
      const playVideo = () => {
        if (video && isVideoLoaded) {
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.warn('Video otomatik oynatma engellendi:', error);
              // iOS'ta sayfa görünür olduğunda tekrar deneme
              document.addEventListener('visibilitychange', () => {
                if (!document.hidden) {
                  video.play().catch(e => console.warn('Video yine oynatılamadı:', e));
                }
              });
            });
          }
        }
      };
      
      // Sayfaya tıklandığında veya dokunulduğunda videoyu oynatmaya çalış
      const handleUserInteraction = () => {
        playVideo();
        // İlk etkileşim sonrası event listener'ı kaldır
        document.removeEventListener('touchstart', handleUserInteraction);
        document.removeEventListener('click', handleUserInteraction);
      };
      
      // iOS'ta kullanıcı etkileşimi event listener'larını ekle
      document.addEventListener('touchstart', handleUserInteraction);
      document.addEventListener('click', handleUserInteraction);
      
      // Sayfa yüklendiğinde videoyu oynatmaya çalış
      playVideo();
      
      return () => {
        video.removeEventListener('loadeddata', loadHandler);
        video.removeEventListener('error', errorHandler);
        document.removeEventListener('touchstart', handleUserInteraction);
        document.removeEventListener('click', handleUserInteraction);
        document.removeEventListener('visibilitychange', playVideo);
        clearTimeout(timeoutId);
      };
    }
  }, [isVideoLoaded]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLatestProductsLoading(true);
      try {
        // En yeni ürünleri getir (sayfalama olmadan, sadece en son 12 ürün)
        const response = await api.get('/products', {
          params: {
            sort: 'newest',
            limit: 12,
            page: 1
          }
        });
        
        // Yeni API yanıt yapısına göre ürünleri al
        const { products } = response.data;
        
        // Eğer products dizisi varsa kullan, yoksa boş dizi kullan
        setLatestProducts(products || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Ürünler yüklenirken bir hata oluştu');
      } finally {
        setIsLatestProductsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchCustomDesigns = async () => {
      setIsCustomDesignsLoading(true);
      try {
        // API çağrısını api.get ile yapalım
        const response = await api.get('/products/custom');
        
        // API yanıtını al
        const customProducts = response.data;
        
        // Eğer customProducts bir dizi ise kullan, değilse boş dizi kullan
        const productsArray = Array.isArray(customProducts) ? customProducts : [];
        
        // Tarihe göre sırala ve son 12 tasarımı al
        const sortedCustomDesigns = productsArray
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 12);
          
        setCustomDesigns(sortedCustomDesigns);
      } catch (error) {
        console.error("Özel tasarımlar getirilirken hata:", error);
        toast.error("Özel tasarımlar yüklenirken bir sorun oluştu");
      } finally {
        setIsCustomDesignsLoading(false);
      }
    };

    fetchCustomDesigns();
  }, []);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      setIsFeaturedProductsLoading(true);
      try {
        // Öne çıkan ürünleri getir
        const response = await api.get('/products', {
          params: {
            is_featured: true,
            limit: 12,
            page: 1
          }
        });
        
        // API yanıt yapısına göre ürünleri al
        const { products } = response.data;
        
        // Eğer products dizisi varsa kullan, yoksa boş dizi kullan
        setFeaturedProducts(products || []);
      } catch (error) {
        console.error('Error fetching featured products:', error);
        toast.error('Öne çıkan ürünler yüklenirken bir hata oluştu');
      } finally {
        setIsFeaturedProductsLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  // Video yükleme işleyicisi
  const handleVideoLoaded = () => {
    console.log("Video yüklendi");
    setIsVideoLoaded(true);
    setVideoError(false);
  };

  // Video yükleme hatası işleyicisi
  const handleVideoError = () => {
    console.error("Video yüklenemedi");
    setVideoError(true);
    setIsVideoLoaded(false);
  };

  useEffect(() => {
    const fetchCollections = async () => {
      setIsCollectionsLoading(true);
      try {
        // Önce koleksiyonları hızlıca yükle
        const { data } = await api.get('/collections');
        setCollections(data);
      } catch (error) {
        console.error('Error fetching collections:', error);
        toast.error('Koleksiyonlar yüklenirken bir hata oluştu');
      } finally {
        setIsCollectionsLoading(false);
      }
    };

    fetchCollections();
  }, []);

  const handleAddToFavorites = async (product) => {
    try {
      await addToFavorites(product);
      trackProductAddToFavorites(product);
      toast.success('Ürün favorilere eklendi');
    } catch (error) {
      console.error('Error adding to favorites:', error);
      toast.error('Favorilere eklenirken bir hata oluştu');
    }
  };

  const handleFavoriteClick = async (product) => {
    try {
      const isInFavorites = favorites.some(fav => fav.product?.id === product.id);
      
      if (isInFavorites) {
        await removeFromFavorites(product.id);
        trackProductRemoveFromFavorites(product);
        toast.success('Ürün favorilerden kaldırıldı');
      } else {
        await addToFavorites(product);
        trackProductAddToFavorites(product);
        toast.success('Ürün favorilere eklendi');
      }
    } catch (error) {
      console.error('Favori işlemi hatası:', error);
      toast.error('İşlem sırasında bir hata oluştu');
    }
  };

  const handleProductClick = (productId) => {
    // Tracking için product bilgisini bul
    const product = latestProducts.find(p => p.id === productId) || 
                   customDesigns.find(p => p.id === productId) || 
                   featuredProducts.find(p => p.id === productId);
    
    if (product) {
      trackProductClick(product);
    }
    
    navigate(`/product/${productId}`);
  };

  // Koleksiyonları 4'erli gruplara ayıran yardımcı fonksiyon
  const chunkCollections = (arr, size) => {
    return Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
      arr.slice(i * size, i * size + size)
    );
  };

  return (
    <div className="home page-transition overflow-hidden">
      {/* Hero Section */}
      <section className="hero-section">
        {!isVideoLoaded && (
          <div className="video-placeholder">
            {/* Düşük kaliteli placeholder görsel veya loading spinner */}
          </div>
        )}
        <video 
          ref={videoRef}
          className={`hero-video ${isVideoLoaded ? 'loaded' : ''}`}
          autoPlay 
          loop 
          muted 
          playsInline
          preload="auto"
          poster="/videos/hero-poster.jpg"
          playsinline="true"
          webkit-playsinline="true"
        >
          <source src="/videos/hero-video.webm" type="video/webm" />
          <source src="/videos/hero-video.mp4" type="video/mp4" />
          Tarayıcınız video oynatmayı desteklemiyor.
        </video>
        <div className="hero-content">
          <h1 className="hero-title">
            Telefonunuz İçin <br /><span className="highlight">En İyi Koruma</span>
          </h1>
          <p className="hero-description">
            Geniş kılıf koleksiyonumuzla telefonunuzu şıklıkla koruyun.
            Premium kalite ve uygun fiyatlarla sizlere sunuyoruz.
          </p>
          <div className="hero-buttons">
            <Link 
              to="/products" 
              className="main-button"
            >
              <FontAwesomeIcon icon={faShoppingBag} className="me-2" />
              Ürünlere Git
            </Link>
            <Link 
              to="/products" 
              className="main-button mobile-hero-button"
            >
              <FontAwesomeIcon icon={faShoppingBag} className="me-1" />
              Ürünlere Git
            </Link>
          </div>
        </div>
      </section>

      {/* Collections Section */}
      <section className="collections-section relative overflow-hidden">
        {/* Paralaks Arka Plan */}
        <div className="absolute inset-0 z-0">
          {/* Arka plan için beyaz overlay */}
          <div className="absolute inset-0 bg-white opacity-90 z-0"></div>
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-amber-50/80 to-white/90 z-1"></div>
        </div>
        
        {/* Dekoratif Şekiller */}
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-amber-300 opacity-20 animate-blob"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-amber-500 opacity-10 animate-blob animation-delay-2000"></div>
        
        <Container className="relative z-10">
          <div className="section-header">
            <h2>Özel Koleksiyonlarımız</h2>
            <p>En yeni ve trend tasarımlarımızı keşfedin</p>
          </div>
          
          {isCollectionsLoading ? (
            <div className="loading-container d-flex justify-content-center align-items-center py-5">
              <div className="loading-content">
                <div className="loading-progress">
                  <span className="loading-dot"></span>
                  <span className="loading-dot"></span>
                  <span className="loading-dot"></span>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop Carousel */}
              <div className="d-none d-md-block">
                <Carousel 
                  interval={3000} 
                  className="collections-carousel"
                  indicators={true}
                  controls={true}
                  pause={false}
                  touch={true}
                >
                  {chunkCollections(collections, 3).map((group, groupIndex) => (
                    <Carousel.Item key={groupIndex}>
                      <Row className="collections-grid">
                        {group.map((collection, index) => (
                          <Col key={collection.id} lg={4} md={4} className="collection-col">
                            <Link 
                              to={`/products?collection=${encodeURIComponent(collection.name)}`}
                              className="collection-card-link"
                              onClick={() => trackCollectionClick(collection)}
                            >
                              <div className="collection-card">
                                <div className="collection-image">
                                  <img 
                                    src={collection.image} 
                                    alt={`${collection.name} koleksiyonu`}
                                    loading={groupIndex === 0 && index === 0 ? "eager" : "lazy"}
                                  />
                                  <div className="collection-overlay">
                                    <div className="overlay-content">
                                      <h3>{collection.name || collection.title}</h3>
                                      <div className="explore-text">
                                        Koleksiyonu Keşfet <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="collection-info">
                                  <div className="collection-description">
                                    {collection.description || "Özel tasarım telefon kılıfları koleksiyonu"}
                                  </div>
                                  <div className="collection-stats">
                                    <div className="collection-date">
                                      {new Date(collection.created_at).toLocaleDateString('tr-TR', {year: 'numeric', month: 'short'})}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Link>
                          </Col>
                        ))}
                      </Row>
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>

              {/* Mobile Grid */}
              <div className="d-md-none">
                <Carousel 
                  interval={3000} 
                  className="collections-carousel mobile-collections-carousel"
                  indicators={true}
                  controls={true}
                  pause={false}
                  touch={true}
                >
                  {chunkCollections(collections, 2).map((group, groupIndex) => (
                    <Carousel.Item key={groupIndex}>
                      <div className="d-flex justify-content-between">
                        {group.map((collection, index) => (
                          <div key={collection.id} style={{ width: '49%' }}>
                            <Link 
                              to={`/products?collection=${encodeURIComponent(collection.name)}`}
                              className="collection-card-link"
                              onClick={() => trackCollectionClick(collection)}
                            >
                              <div className="mobile-collection-card">
                                <img 
                                  src={collection.image} 
                                  alt={`${collection.name} koleksiyonu`}
                                  loading={groupIndex === 0 && index === 0 ? "eager" : "lazy"}
                                  className="mobile-collection-image"
                                />
                                <div className="mobile-collection-overlay">
                                  <h3 className="mobile-collection-title">{collection.name || collection.title}</h3>
                                </div>
                              </div>
                            </Link>
                          </div>
                        ))}
                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>
            </>
          )}
        </Container>
      </section>

      {/* Featured Products Section */}
      <section className="latest-products-section featured-products-section py-24 relative">
        {/* Paralaks Arka Plan */}
        <div className="absolute inset-0 z-0">
          {/* Arka plan için beyaz overlay */}
          <div className="absolute inset-0 bg-white opacity-90 z-0"></div>
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 to-amber-50/90 z-1"></div>
        </div>
        
        {/* Dekoratif Şekiller */}
        <div className="absolute top-40 right-20 w-32 h-32 rounded-full bg-amber-300 opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute bottom-40 left-20 w-40 h-40 rounded-full bg-amber-500 opacity-10 animate-blob"></div>
        
        <Container className="relative z-10">
          <div className="section-header">
            <h2>Öne Çıkan</h2>
            <p>Öne Çıkan Ürünlerimiz</p>
          </div>

          {isFeaturedProductsLoading ? (
            <div className="loading-container d-flex justify-content-center align-items-center py-5">
              <div className="loading-content">
                <div className="loading-progress">
                  <span className="loading-dot"></span>
                  <span className="loading-dot"></span>
                  <span className="loading-dot"></span>
                </div>
              </div>
            </div>
          ) : featuredProducts.length > 0 ? (
            <>
              <div className="d-none d-md-block">
                <Carousel
                  interval={2000}
                  indicators={true}
                  controls={true}
                  pause={false}
                  touch={true}
                  className="products-carousel"
                >
                  {chunkArray(featuredProducts, 3).map((group, index) => (
                    <Carousel.Item key={index}>
                      <div className="d-flex justify-content-between">
                        {group.map((product) => (
                          <div 
                            key={product.id} 
                            className="product-card"
                            onClick={() => handleProductClick(product.id)}
                            style={{ cursor: 'pointer', width: '32%' }}
                          >
                            <div className="product-image-wrapper">
                              <img
                                src={product.images}
                                alt={product.name}
                                className="product-image"
                                loading="lazy"
                              />
                              <div className="product-overlay">
                                <button 
                                  className={`favorite-btn ${favorites.some(fav => fav.product?.id === product.id) ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFavoriteClick(product);
                                  }}
                                >
                                  <FontAwesomeIcon 
                                    icon={favorites.some(fav => fav.product?.id === product.id) ? fasHeart : farHeart} 
                                  />
                                </button>
                              </div>
                              
                              {/* Featured Badge */}
                              <div className="featured-badge">
                                <FontAwesomeIcon icon={faStar} className="featured-icon" />
                                <span className="featured-text">Öne Çıkan</span>
                              </div>
                              
                              {/* İndirim Etiketi */}
                              <div className="discount-badge">
                                <FontAwesomeIcon icon={faTag} className="discount-icon" />
                                <span className="discount-text">%33 İndirim</span>
                              </div>
                            </div>
                            <div className="product-info">
                              <h3>{product.name}</h3>
                              <p className="product-price">{product.price} TL</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>

              <div className="d-md-none">
                <Carousel
                  interval={2000}
                  indicators={true}
                  controls={true}
                  pause={false}
                  touch={true}
                  className="products-carousel"
                >
                  {chunkArray(featuredProducts, 2).map((group, index) => (
                    <Carousel.Item key={index}>
                      <div className="d-flex justify-content-between">
                        {group.map((product) => (
                          <div 
                            key={product.id} 
                            className="product-card"
                            onClick={() => handleProductClick(product.id)}
                            style={{ cursor: 'pointer', width: '49%' }}
                          >
                            <div className="product-image-wrapper">
                              <img
                                src={product.images}
                                alt={product.name}
                                className="product-image"
                                loading="lazy"
                              />
                              <div className="product-overlay">
                                <button 
                                  className={`favorite-btn ${favorites.some(fav => fav.product?.id === product.id) ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFavoriteClick(product);
                                  }}
                                >
                                  <FontAwesomeIcon 
                                    icon={favorites.some(fav => fav.product?.id === product.id) ? fasHeart : farHeart} 
                                  />
                                </button>
                              </div>
                              
                              {/* Featured Badge */}
                              <div className="featured-badge">
                                <FontAwesomeIcon icon={faStar} className="featured-icon" />
                                <span className="featured-text">Öne Çıkan</span>
                              </div>
                              
                              {/* İndirim Etiketi */}
                              <div className="discount-badge">
                                <FontAwesomeIcon icon={faTag} className="discount-icon" />
                                <span className="discount-text">%33 İndirim</span>
                              </div>
                            </div>
                            <div className="product-info">
                              <h3>{product.name}</h3>
                              <p className="product-price">{product.price} TL</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>
            </>
          ) : (
            <div className="text-center mt-4">
              <p>Henüz öne çıkan ürün bulunmamaktadır.</p>
            </div>
          )}
        </Container>
      </section>

      {/* Latest Products Section */}
      <section className="latest-products-section py-24 relative">
        {/* Paralaks Arka Plan */}
        <div className="absolute inset-0 z-0">
          {/* Arka plan için beyaz overlay */}
          <div className="absolute inset-0 bg-white opacity-90 z-0"></div>
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 to-amber-50/90 z-1"></div>
        </div>
        
        {/* Dekoratif Şekiller */}
        <div className="absolute top-40 right-20 w-32 h-32 rounded-full bg-amber-300 opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute bottom-40 left-20 w-40 h-40 rounded-full bg-amber-500 opacity-10 animate-blob"></div>
        
        <Container className="relative z-10">
          <div className="section-header">
            <h2>En Yeni</h2>
            <p>En Son Çıkan Ürünlerimiz</p>
          </div>

          {isLatestProductsLoading ? (
            <div className="loading-container d-flex justify-content-center align-items-center py-5">
              <div className="loading-content">
                <div className="loading-progress">
                  <span className="loading-dot"></span>
                  <span className="loading-dot"></span>
                  <span className="loading-dot"></span>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="d-none d-md-block">
                <Carousel
                  interval={2000}
                  indicators={true}
                  controls={true}
                  pause={false}
                  touch={true}
                  className="products-carousel"
                >
                  {chunkArray(latestProducts, 3).map((group, index) => (
                    <Carousel.Item key={index}>
                      <div className="d-flex justify-content-between">
                        {group.map((product) => (
                          <div 
                            key={product.id} 
                            className="product-card"
                            onClick={() => handleProductClick(product.id)}
                            style={{ cursor: 'pointer', width: '32%' }}
                          >
                            <div className="product-image-wrapper">
                              <img
                                src={product.images}
                                alt={product.name}
                                className="product-image"
                                loading="lazy"
                              />
                              <div className="product-overlay">
                                <button 
                                  className={`favorite-btn ${favorites.some(fav => fav.product?.id === product.id) ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFavoriteClick(product);
                                  }}
                                >
                                  <FontAwesomeIcon 
                                    icon={favorites.some(fav => fav.product?.id === product.id) ? fasHeart : farHeart} 
                                  />
                                </button>
                              </div>
                              
                              {/* İndirim Etiketi */}
                              {product.discount_rate && (
                                <div className="discount-badge">
                                  <FontAwesomeIcon icon={faTag} className="discount-icon" />
                                  <span className="discount-text">%{product.discount_rate} İndirim</span>
                                </div>
                              )}
                            </div>
                            <div className="product-info">
                              <h3>{product.name}</h3>
                              <p className="product-price">{product.price} TL</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>

              <div className="d-md-none">
                <Carousel
                  interval={2000}
                  indicators={true}
                  controls={true}
                  pause={false}
                  touch={true}
                  className="products-carousel"
                >
                  {chunkArray(latestProducts, 2).map((group, index) => (
                    <Carousel.Item key={index}>
                      <div className="d-flex justify-content-between">
                        {group.map((product) => (
                          <div 
                            key={product.id} 
                            className="product-card"
                            onClick={() => handleProductClick(product.id)}
                            style={{ cursor: 'pointer', width: '49%' }}
                          >
                            <div className="product-image-wrapper">
                              <img
                                src={product.images}
                                alt={product.name}
                                className="product-image"
                                loading="lazy"
                              />
                              <div className="product-overlay">
                                <button 
                                  className={`favorite-btn ${favorites.some(fav => fav.product?.id === product.id) ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFavoriteClick(product);
                                  }}
                                >
                                  <FontAwesomeIcon 
                                    icon={favorites.some(fav => fav.product?.id === product.id) ? fasHeart : farHeart} 
                                  />
                                </button>
                              </div>
                              
                              {/* İndirim Etiketi */}
                              {product.discount_rate && (
                                <div className="discount-badge">
                                  <FontAwesomeIcon icon={faTag} className="discount-icon" />
                                  <span className="discount-text">%{product.discount_rate} İndirim</span>
                                </div>
                              )}
                            </div>
                            <div className="product-info">
                              <h3>{product.name}</h3>
                              <p className="product-price">{product.price} TL</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>
            </>
          )}
        </Container>
      </section>

      {/* Müşteri Tasarımları - Sizden Gelenler Section */}
      {(isCustomDesignsLoading || customDesigns.length > 0) && (
        <section className="latest-products-section custom-designs-section py-24 relative">
          {/* Paralaks Arka Plan */}
          <div className="absolute inset-0 z-0">
            {/* Arka plan için beyaz overlay */}
            <div className="absolute inset-0 bg-white opacity-90 z-0"></div>
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/80 to-amber-50/90 z-1"></div>
          </div>
          
          {/* Dekoratif Şekiller */}
          <div className="absolute top-40 right-20 w-32 h-32 rounded-full bg-amber-300 opacity-20 animate-blob animation-delay-4000"></div>
          <div className="absolute bottom-40 left-20 w-40 h-40 rounded-full bg-amber-500 opacity-10 animate-blob"></div>
          
          <Container className="relative z-10">
            <div className="section-header">
              <h2>Müşteri Tasarımları</h2>
              <p>Sizden Gelenler</p>
            </div>

            {isCustomDesignsLoading ? (
              <div className="loading-container d-flex justify-content-center align-items-center py-5">
                <div className="loading-content">
                  <p className="loading-text">Müşteri Tasarımları Yükleniyor</p>
                  <div className="loading-progress">
                    <span className="loading-dot"></span>
                    <span className="loading-dot"></span>
                    <span className="loading-dot"></span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="d-none d-md-block">
                  <Carousel
                    interval={2000}
                    indicators={true}
                    controls={true}
                    pause={false}
                    touch={true}
                    className="products-carousel"
                  >
                    {chunkArray(customDesigns, 3).map((group, index) => (
                      <Carousel.Item key={index}>
                        <div className="d-flex justify-content-between">
                          {group.map((product) => (
                            <div 
                              key={product.id} 
                              className="product-card"
                              onClick={() => handleProductClick(product.id)}
                              style={{ cursor: 'pointer', width: '32%' }}
                            >
                              <div className="product-image-wrapper">
                                <img
                                  src={product.images}
                                  alt={product.name}
                                  className="product-image"
                                  loading="lazy"
                                />
                                <div className="product-overlay">
                                  <button 
                                    className={`favorite-btn ${favorites.some(fav => fav.product?.id === product.id) ? 'active' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFavoriteClick(product);
                                    }}
                                  >
                                    <FontAwesomeIcon 
                                      icon={favorites.some(fav => fav.product?.id === product.id) ? fasHeart : farHeart} 
                                    />
                                  </button>
                                </div>
                                
                                {/* İndirim Etiketi */}
                                {product.discount_rate && (
                                  <div className="discount-badge">
                                    <FontAwesomeIcon icon={faTag} className="discount-icon" />
                                    <span className="discount-text">%{product.discount_rate} İndirim</span>
                                  </div>
                                )}
                              </div>
                              <div className="product-info">
                                <h3>{product.name}</h3>
                                <p className="product-price">{product.price} TL</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Carousel.Item>
                    ))}
                  </Carousel>
                </div>

                <div className="d-md-none">
                  <Carousel
                    interval={2000}
                    indicators={true}
                    controls={true}
                    pause={false}
                    touch={true}
                    className="products-carousel"
                  >
                    {chunkArray(customDesigns, 2).map((group, index) => (
                      <Carousel.Item key={index}>
                        <div className="d-flex justify-content-between">
                          {group.map((product) => (
                            <div 
                              key={product.id} 
                              className="product-card"
                              onClick={() => handleProductClick(product.id)}
                              style={{ cursor: 'pointer', width: '49%' }}
                            >
                              <div className="product-image-wrapper">
                                <img
                                  src={product.images}
                                  alt={product.name}
                                  className="product-image"
                                  loading="lazy"
                                />
                                <div className="product-overlay">
                                  <button 
                                    className={`favorite-btn ${favorites.some(fav => fav.product?.id === product.id) ? 'active' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFavoriteClick(product);
                                    }}
                                  >
                                    <FontAwesomeIcon 
                                      icon={favorites.some(fav => fav.product?.id === product.id) ? fasHeart : farHeart} 
                                    />
                                  </button>
                                </div>
                                
                                {/* İndirim Etiketi */}
                                {product.discount_rate && (
                                  <div className="discount-badge">
                                    <FontAwesomeIcon icon={faTag} className="discount-icon" />
                                    <span className="discount-text">%{product.discount_rate} İndirim</span>
                                  </div>
                                )}
                              </div>
                              <div className="product-info">
                                <h3>{product.name}</h3>
                                <p className="product-price">{product.price} TL</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Carousel.Item>
                    ))}
                  </Carousel>
                </div>
              </>
            )}
          </Container>
        </section>
      )}

      {/* Features Section */}
      <section className="features-section py-24 relative">
        {/* Paralaks Arka Plan */}
        <div className="absolute inset-0 z-0">
          {/* Arka plan için beyaz overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-50 to-white opacity-90 z-0"></div>
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-amber-50/30 to-white/50 z-1"></div>
        </div>
        
        {/* Dekoratif Şekiller */}
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-amber-100 opacity-30 rounded-bl-full"></div>
        <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-amber-200 opacity-20 rounded-tr-full"></div>
        
        <Container className="relative z-10">
          <div className="section-header">
            <h2>Neden Bizi Tercih Etmelisiniz?</h2>
            <p>Size en iyi hizmeti sunmak için çalışıyoruz.</p>
          </div>
          <Row className="features-grid">
            <Col md={4}>
              <div className="feature-card">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faShippingFast} />
                </div>
                <h3 className="feature-title">Hızlı Teslimat</h3>
                <p className="feature-description">Siparişleriniz aynı gün kargoya verilir ve en kısa sürede size ulaştırılır.</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="feature-card">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faLock} />
                </div>
                <h3 className="feature-title">Güvenli Ödeme</h3>
                <p className="feature-description">256-bit SSL sertifikası ile güvenli ödeme işlemleri gerçekleştirin.</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="feature-card">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faHeadset} />
                </div>
                <h3 className="feature-title">7/24 Destek</h3>
                <p className="feature-description">Müşteri hizmetlerimiz her zaman yanınızda ve sorularınızı yanıtlamaya hazır.</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Özel CSS Animasyonları için stil */}
      <style jsx>{`
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        
        /* Mobil koleksiyon kartları için özel stiller */
        .mobile-collection-card {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          margin-bottom: 10px;
          height: 220px;
        }
        
        .mobile-collection-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }
        
        .mobile-collection-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0) 100%);
          padding: 15px 10px;
          text-align: center;
        }
        
        .mobile-collection-title {
          color: white;
          font-size: 16px;
          font-weight: 600;
          margin: 0;
          text-shadow: 0 1px 3px rgba(0,0,0,0.3);
        }
        
        .mobile-collections-carousel .carousel-indicators {
          bottom: -10px;
        }
      `}</style>
      
      {/* Kampanya pop-up'ı */}
      <CampaignPopup />
    </div>
  );
};

export default Home;
